import {
    Avatar, Box, Button, Chip, CircularProgress, Stack,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tooltip, Typography,
} from "@mui/material";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import AssignmentOutlinedIcon from "@mui/icons-material/AssignmentOutlined";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import StatCard from "../paymenthistory/common/StatCard";
import { useWorkTracker } from "../../hooks/useBriefs";
import { STATUS_OPTIONS } from "./briefOptions";
import { STATUS_COLORS, colorForName, initialsFor, formatBudget, labelForStatus } from "./briefUiUtils";

// One row per person who currently has at least one brief — busiest first
// (the backend already sorts by activeCount desc), so a manager can spot
// who's overloaded without hunting through an alphabetical list.
function WorkTracker() {
    const { data, isLoading, isError, error, refetch } = useWorkTracker();
    const people = data?.people ?? [];
    const summary = data?.summary;

    if (isError) {
        return (
            <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography sx={{ fontWeight: 700, mb: 0.5 }}>Couldn't load Work Tracker</Typography>
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                    {error?.response?.data?.message || 'Something went wrong on our end.'}
                </Typography>
                <Button variant="outlined" onClick={() => refetch()} sx={{ textTransform: 'none', borderRadius: '10px' }}>
                    Try again
                </Button>
            </Box>
        );
    }

    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                <CircularProgress sx={{ color: '#FF5200' }} />
            </Box>
        );
    }

    if (!people.length) {
        return (
            <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography sx={{ fontWeight: 700, mb: 0.5 }}>No workload yet</Typography>
                <Typography color="text.secondary">
                    Once briefs have assignees, their workload shows up here.
                </Typography>
            </Box>
        );
    }

    return (
        <Box>
            <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: 'wrap', rowGap: 2 }}>
                <StatCard heading="Team Members" value={summary.totalPeople} icon={<PeopleAltOutlinedIcon sx={{ fontSize: 18 }} />} />
                <StatCard heading="Active Briefs" value={summary.totalActiveBriefs} icon={<AssignmentOutlinedIcon sx={{ fontSize: 18 }} />} />
                <StatCard heading="Total Briefs" value={summary.totalBriefs} icon={<ListAltOutlinedIcon sx={{ fontSize: 18 }} />} accentColor="#6366F1" />
                <StatCard heading="Overdue" value={summary.totalOverdue} icon={<WarningAmberOutlinedIcon sx={{ fontSize: 18 }} />} accentColor="#EF4444" />
            </Stack>

            <TableContainer
                sx={{
                    borderRadius: '16px',
                    border: '1px solid #E5E7EB',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.04)',
                    background: '#fff',
                }}
            >
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{ fontWeight: 700, fontSize: 12.5, color: '#6B7280' }}>Person</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: 12.5, color: '#6B7280' }}>Active workload</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: 12.5, color: '#6B7280' }}>By status</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: 12.5, color: '#6B7280' }}>Overdue</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: 12.5, color: '#6B7280' }}>Budget</TableCell>
                            <TableCell sx={{ fontWeight: 700, fontSize: 12.5, color: '#6B7280' }}>Reviewer(s)</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {people.map((person) => (
                            <TableRow key={person.userId} hover>
                                <TableCell>
                                    <Stack direction="row" alignItems="center" spacing={1.25}>
                                        <Avatar sx={{ width: 30, height: 30, fontSize: 12.5, fontWeight: 700, bgcolor: colorForName(person.name) }}>
                                            {initialsFor(person.name)}
                                        </Avatar>
                                        <Typography sx={{ fontWeight: 700, fontSize: 13.5 }}>{person.name}</Typography>
                                    </Stack>
                                </TableCell>
                                <TableCell>
                                    <Typography sx={{ fontWeight: 800, fontSize: 15 }}>{person.activeCount}</Typography>
                                    <Typography sx={{ fontSize: 11.5, color: '#9CA3AF' }}>{person.total} total</Typography>
                                </TableCell>
                                <TableCell>
                                    <Stack direction="row" spacing={0.5} flexWrap="wrap" rowGap={0.5}>
                                        {STATUS_OPTIONS.filter((s) => person.byStatus[s] > 0).map((s) => {
                                            const style = STATUS_COLORS[s] || STATUS_COLORS.Pending;
                                            return (
                                                <Chip
                                                    key={s}
                                                    size="small"
                                                    label={`${labelForStatus(s)}: ${person.byStatus[s]}`}
                                                    sx={{ bgcolor: style.bg, color: style.color, fontWeight: 700, fontSize: 11, height: 22 }}
                                                />
                                            );
                                        })}
                                    </Stack>
                                </TableCell>
                                <TableCell>
                                    {person.overdue > 0 ? (
                                        <Chip
                                            size="small"
                                            label={person.overdue}
                                            sx={{ bgcolor: '#FEE2E2', color: '#991B1B', fontWeight: 800, fontSize: 11.5, height: 22, minWidth: 30 }}
                                        />
                                    ) : (
                                        <Typography sx={{ color: '#9CA3AF', fontSize: 13 }}>—</Typography>
                                    )}
                                </TableCell>
                                <TableCell>
                                    <Typography sx={{ fontSize: 13, fontWeight: 600 }}>₹{formatBudget(person.totalBudget)}</Typography>
                                </TableCell>
                                <TableCell>
                                    {person.reviewers.length ? (
                                        <Stack direction="row" spacing={0.5} flexWrap="wrap" rowGap={0.5}>
                                            {person.reviewers.map((r) => (
                                                <Tooltip key={r.userId} title="Reviews this person's briefs">
                                                    <Chip size="small" label={r.name} sx={{ fontSize: 11, height: 22 }} />
                                                </Tooltip>
                                            ))}
                                        </Stack>
                                    ) : (
                                        <Typography sx={{ color: '#9CA3AF', fontSize: 13 }}>—</Typography>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
}

export default WorkTracker;
